import { SmButton, SmIcon } from '@smiles/smiles-react-design-system';
import React, { useMemo, useState } from 'react';
import { Calendar, CalendarInput, Checkbox, SearchInput, TimeInputDropdown } from '../../components';
import { useContextSelector } from '../../hooks';

import type { CarSearchFilters, GetSearchLocation, ItineraryData, RentalType, SearchInputType } from '../../types';

interface ICarSearchDesktopProps {
  filterSearchBy: CarSearchFilters;
  onLocationSelect: (location: GetSearchLocation, type: SearchInputType) => void;
  onDatesSelect: (dates: ItineraryData) => void;
  onTimeSelect: (time: string, flag: RentalType) => void;
  onDifferentAgencyCheck: (id: string) => void;
  onSubmit: () => void;
}

export const Desktop = ({
  filterSearchBy,
  onLocationSelect,
  onDatesSelect,
  onTimeSelect,
  onDifferentAgencyCheck,
  onSubmit,
  ...props
}: ICarSearchDesktopProps) => {
  const {
    errors: { datesError, locationError, timeError },
    search: { differentAgencies },
  } = useContextSelector();
  const [isCalendarOpen, setIsCalendarOpen] = useState<boolean>(false);
  const [calendarInputValues, setCalendarInputValues] = useState<ItineraryData>({ pickup: null, dropoff: null });

  const hasError = useMemo(() => {
    const hasLocationError = Object.values(locationError).some((error) => error.active);
    const hasDatesError = Object.values(datesError).some((error) => error.active);
    const hasTimeError = Object.values(timeError).some((error) => error.active);

    return hasLocationError || hasDatesError || hasTimeError;
  }, [locationError, datesError, timeError]);

  const handleCalendarToggle = (isOpen: boolean) => {
    setIsCalendarOpen(isOpen);
  };

  return (
    <>
      <section className="car-search-content-wrapper" {...props}>
        {/* SEARCH INPUT */}
        {differentAgencies ? (
          <section className="car-search-input-wrapper">
            <SearchInput
              id="pickup"
              label="Onde você quer alugar?"
              placeholder="Local de retirada"
              type="pickup"
              debounceDelay={250}
              filterSearchBy={filterSearchBy}
              onLocationSelect={onLocationSelect}
            />

            <SmIcon className="car-search-input-icon" name="compare_arrows" type="round" />

            <SearchInput
              id="dropoff"
              placeholder="Local de devolução"
              type="dropoff"
              debounceDelay={250}
              filterSearchBy={filterSearchBy}
              onLocationSelect={onLocationSelect}
            />
          </section>
        ) : (
          <SearchInput
            id="oneway"
            label="Onde você quer alugar?"
            placeholder="Digite o local ou aeroporto"
            debounceDelay={250}
            filterSearchBy={filterSearchBy}
            onLocationSelect={onLocationSelect}
          />
        )}

        {/* CALENDAR + TIME SELECT PICKUP */}
        <section className="car-search-calendar-wrapper">
          <CalendarInput
            id="pickup"
            type="pickup"
            label="Retirada"
            placeholder="Data"
            onCalendarToggle={handleCalendarToggle}
          />

          <TimeInputDropdown id="pickup" type="pickup" placeholder="Hora" onTimeSelect={onTimeSelect} />
        </section>

        {/* CALENDAR + TIME SELECT DROPOFF */}
        <section className="car-search-calendar-wrapper">
          <CalendarInput
            id="dropoff"
            type="dropoff"
            label="Devolução"
            placeholder="Data"
            onCalendarToggle={handleCalendarToggle}
          />

          <TimeInputDropdown id="dropoff" type="dropoff" placeholder="Hora" onTimeSelect={onTimeSelect} />
        </section>

        {/* SEARCH BUTTON */}
        <SmButton className="car-search-submit-button" text="Buscar" onClick={onSubmit} />

        {/* SINGLE CALENDAR MODAL FOR PICKUP AND DROPOFF DATES INPUTS */}
        <Calendar
          id="dates"
          isOpen={isCalendarOpen}
          onCalendarClose={() => handleCalendarToggle(false)}
          onDatesSelect={onDatesSelect}
        />
      </section>

      {/* CHECKBOX */}
      <Checkbox
        containerClassName={`car-search-different-agency-container${hasError ? ' extra-margin' : ''}`}
        labelClassName="car-search-different-agency-label"
        id="car-search-different-agency-checkbox"
        label="Devolver em outro local"
        labelPosition="right"
        checked={differentAgencies}
        onCheck={onDifferentAgencyCheck}
      />
    </>
  );
};
